import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DropZoneComponent from '../../../components/common/DropZone';
import { IMG_SCALE_SIZE } from '../../../utility/constants';
import { BodyText } from '@paypalcorp/pp-react-text';
import { Checkbox } from '@paypalcorp/pp-react-checkbox';
import { Button } from '@paypalcorp/pp-react-buttons';
import { change } from 'redux-form';
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { TrashIcon } from '@paypalcorp/pp-react-icons';
import { Alert } from '@paypalcorp/pp-react-alert';
import _isEmpty from 'lodash/isEmpty';
import {
  LOGO_TARGET_BUTTON,
  LOGO_TARGET_NOLOGO,
  LOGO_TARGET_PROFILE,
} from '../../reducers/reducerButtonFactoryV3';

import getLocaleMessages from '../../../buttons/utils/localesMessages';
import { trackEventWithData } from '../../utilsV3/analytics';
import { FPTI_SPEC, SCREEN } from '../../utilsV3/fptiConstants';
import { getUserType } from '../../utils/buttonFactoryUtils';
const errors = getLocaleMessages('buttonsV3/fieldErrors');

const DropzoneLogo = ({
  dispatch,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  getContent,
  closeModal,
  logoUrl,
  setLogoImg,
  logoTarget,
  isLogoBusinessProfile,
  setIsLogoBusinessProfile,
}) => {
  const logo = getContent('modal.logo');
  const checkbox = getContent('modal.checkbox', null, { useHTML: true });
  const checkboxHelpText = getContent('modal.checkboxHelpText');
  const saveButton = getContent('modal.saveButton');
  const deleteButton = getContent('modal.deleteButton');
  const userType = getUserType();

  const [imageData, setImageData] = useState();
  const [showSpinner, setShowSpinner] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(true);
  const [imageUpdated, setImageUpdated] = useState(false);
  const [dropzoneError, setDropzoneError] = useState('');
  const [showSaveButton, setShowSaveButton] = useState(false);

  const handleImageDropzone = (image) => {
    setDropzoneError('');
    if (image.hasError) {
      setDropzoneError(image.error);
      trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
        error_code: 'IMAGE_DROPZONE_ERROR',
        error_desc: 'logo',
        error_detail: image.error,
        screen: SCREEN.EXPERIENCE_SETUP,
      });
    } else {
      trackEventWithData(FPTI_SPEC.UPLOAD_LOGO_MODAL_IMAGE_UPDATED);
      setImageUpdated(true);
      setImageData(image);
      setIsLogoBusinessProfile(false);
    }
  };

  useEffect(() => {
    if (logoUrl) {
      setImageData({
        src: logoUrl,
        name: logoUrl.split('/').pop(),
      });
    }
  }, []);

  const handleImageUpload = async () => {
    return new Promise((resolve, reject) => {
      uploadImage(imageData.src)
        .then((response) => {
          if (response.ok) {
            return resolve(response);
          }

          let error = {
            processingStage: 'IMAGE_UPLOAD_ERROR',
            response,
          };
          trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
            error_code: 'LOGO_IMAGE_UPLOAD_ERROR',
            error_desc: 'logo',
            error_detail: JSON.stringify(response),
            screen: SCREEN.EXPERIENCE_SETUP,
          });
          return reject(error);
        })
        .catch((error) => {
          trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
            error_code: 'LOGO_IMAGE_UPLOAD_ERROR',
            error_desc: 'logo',
            error_detail: JSON.stringify(error),
            screen: SCREEN.EXPERIENCE_SETUP,
          });
          return reject({ ...error, processingStage: 'IMAGE_UPLOAD_ERROR' });
        });
    });
  };

  const handleBusinessProfileUpdate = async (url) => {
    return new Promise((resolve, reject) => {
      updateBusinessProfileLogo(url)
        .then((response) => {
          if (response.ok) {
            return resolve(response);
          }

          let error = {
            processingStage: 'BUSINESS_PROFILE_IMAGE_UPDATE_ERROR',
            response,
          };
          return reject(error);
        })
        .catch((error) => {
          return reject({
            ...error,
            processingStage: 'BUSINESS_PROFILE_IMAGE_UPDATE_ERROR',
          });
        });
    });
  };

  const updateLogo = (url) => {
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoUrl', url));
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_BUTTON));
    setLogoImg(url);
    setShowSpinner(false);
    setIsLogoBusinessProfile(false);
    closeModal();
  };

  const updateSettingsLogo = (url) => {
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoUrl', ''));
    dispatch(change(BTN_FACTORY_V3_FORM, 'businessProfileLogoUrl', url));
    updateBusinessImageUrls(url);
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_PROFILE));
    setIsLogoBusinessProfile(true);
    setLogoImg(url);
    setShowSpinner(false);
    closeModal();
  };

  const removeImage = () => {
    setImageData();
    setIsLogoBusinessProfile(true);
    setShowSaveButton(true);
  };

  const saveLogo = async () => {
    trackEventWithData(FPTI_SPEC.UPLOAD_LOGO_MODAL_SAVE_CL);

    let src = imageData?.src ?? '';
    if (_isEmpty(src)) {
      dispatch(change(BTN_FACTORY_V3_FORM, 'logoUrl', ''));
      dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_NOLOGO));
      closeModal();
    } else if (imageUpdated) {
      try {
        setShowSpinner(true);
        let response = await handleImageUpload();
        if (response.success) {
          // BUSINESS PROFILE LOGO UPDATE
          if (checkboxValue && userType === 'BUSINESS') {
            try {
              let businessProfileResponse = await handleBusinessProfileUpdate(
                response.src
              );
              if (businessProfileResponse.success) {
                updateSettingsLogo(response.src);
              } else {
                updateLogo(response.src);
                // TODO: Manage business profile error (Content is in progress)
              }
            } catch (e) {
              updateLogo(response.src);
            }
          } else {
            updateLogo(response.src);
          }
        } else {
          setShowSpinner(false);
          setDropzoneError(errors.dropzone.tryAgain);
          setLogoImg('');
        }
      } catch (e) {
        setShowSpinner(false);
        setDropzoneError(errors.dropzone.tryAgain);
        setLogoImg('');
      }
    } else {
      closeModal();
    }
  };

  return (
    <div>
      {imageData && (
        <>
          <BodyText strong>{logo}</BodyText>
          <div className="image-name-delete">
            <BodyText className="dropzone-image-name">
              {imageData.name}
            </BodyText>
            <IconButton
              onClick={removeImage}
              svgIconComponent={TrashIcon}
              className="delete-img"
            >
              {deleteButton}
            </IconButton>
          </div>
        </>
      )}
      <DropZoneComponent
        imgURL={imageData?.src ?? ''}
        onChange={handleImageDropzone}
        imgWidth={IMG_SCALE_SIZE.WIDTH_LOGO}
        showPreviewInDropzone={true}
        displayEditIcon={true}
        setDefaultHeight={logoTarget === LOGO_TARGET_PROFILE ? true : false}
        showSolidBorder={true}
      />
      {imageUpdated ? (
        <>
          {!isLogoBusinessProfile && userType === 'BUSINESS' && (
            <Checkbox
              label={checkbox}
              checked={checkboxValue}
              onChange={() => {
                trackEventWithData(FPTI_SPEC.UPLOAD_LOGO_MODAL_CHECKBOX_CL, {
                  checked: !checkboxValue,
                });
                setCheckboxValue(!checkboxValue);
              }}
              className="business-checkbox"
              name="updateBusinessProfile"
              helperText={checkboxHelpText}
            />
          )}
          {dropzoneError && (
            <Alert type="warning" className="dropzone-error">
              {dropzoneError}
            </Alert>
          )}
          <Button
            onClick={saveLogo}
            className="save-button"
            btnState={showSpinner ? 'processing' : 'initial'}
          >
            {saveButton}
          </Button>
        </>
      ) : (
        <>
          {dropzoneError && (
            <Alert type="warning" className="dropzone-error">
              {dropzoneError}
            </Alert>
          )}
          {showSaveButton && (
            <Button
              onClick={saveLogo}
              className="save-button"
              btnState={showSpinner ? 'processing' : 'initial'}
            >
              {saveButton}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

DropzoneLogo.propTypes = {
  dispatch: PropTypes.func,
  uploadImage: PropTypes.func,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  getContent: PropTypes.func,
  closeModal: PropTypes.func,
  logoUrl: PropTypes.string,
  setLogoImg: PropTypes.func,
  logoTarget: PropTypes.string,
  isLogoBusinessProfile: PropTypes.bool,
  setIsLogoBusinessProfile: PropTypes.func,
};

export default DropzoneLogo;
